let ready;
const request = (r) => new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
});
const complete = (t) => new Promise((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onabort = () => reject(t.error ?? new Error("Local storage transaction aborted"));
    t.onerror = () => reject(t.error);
});
async function encrypt(key, name, value) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: new TextEncoder().encode(name) }, key, new TextEncoder().encode(JSON.stringify(value)));
    return { format: "aes-gcm-v1", iv, data };
}
async function decrypt(key, name, value) {
    if (value.format !== "aes-gcm-v1")
        throw Error("Unsupported local record format");
    const bytes = await crypto.subtle.decrypt({
        name: "AES-GCM",
        iv: value.iv,
        additionalData: new TextEncoder().encode(name),
    }, key, value.data);
    return JSON.parse(new TextDecoder().decode(bytes));
}
async function initialise() {
    const r = indexedDB.open("coralclean-phase1", 2);
    r.onupgradeneeded = () => {
        if (!r.result.objectStoreNames.contains("local"))
            r.result.createObjectStore("local", { keyPath: "key" });
        if (!r.result.objectStoreNames.contains("keys"))
            r.result.createObjectStore("keys");
    };
    const db = await request(r);
    db.onversionchange = () => {
        db.close();
        ready = undefined;
    };
    const priorRows = await request(db.transaction("local").objectStore("local").getAll());
    const candidate = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    const tx = db.transaction("keys", "readwrite"), done = complete(tx), store = tx.objectStore("keys");
    const existing = await request(store.get("profile-v1"));
    if (!existing && priorRows.some((row) => row.encrypted)) {
        await done;
        db.close();
        throw Error("Offline encryption key is missing. Local work is preserved; contact your supervisor before clearing storage.");
    }
    const key = existing ?? candidate;
    if (!existing)
        store.add(key, "profile-v1");
    await done;
    // Prepare encryption before the atomic compare-and-replace. A concurrent writer
    // can never be overwritten by migration, and failed encryption keeps the original.
    const read = db.transaction("local"), rows = await request(read.objectStore("local").getAll());
    for (const row of rows) {
        if (row.encrypted)
            continue;
        const encrypted = await encrypt(key, row.key, row.value);
        await decrypt(key, row.key, encrypted);
        const write = db.transaction("local", "readwrite"), saved = complete(write), local = write.objectStore("local");
        const current = await request(local.get(row.key));
        if (current &&
            !current.encrypted &&
            JSON.stringify(current.value) === JSON.stringify(row.value))
            local.put({ key: row.key, encrypted });
        await saved;
    }
    return { db, key };
}
function open() {
    return (ready ??= initialise().catch((error) => {
        ready = undefined;
        throw error;
    }));
}
export async function get(name) {
    const { db, key } = await open();
    const row = await request(db.transaction("local").objectStore("local").get(name));
    return row ? await decrypt(key, name, row.encrypted) : undefined;
}
export async function put(name, value) {
    const { db, key } = await open(), encrypted = await encrypt(key, name, value);
    const tx = db.transaction("local", "readwrite"), done = complete(tx);
    tx.objectStore("local").put({ key: name, encrypted });
    await done;
}
export async function remove(name) {
    const { db } = await open(), tx = db.transaction("local", "readwrite"), done = complete(tx);
    tx.objectStore("local").delete(name);
    await done;
}
export async function entries(prefix) {
    const { db, key } = await open(), rows = await request(db.transaction("local").objectStore("local").getAll());
    return Promise.all(rows
        .filter((row) => row.key.startsWith(prefix))
        .map(async (row) => ({
        key: row.key,
        value: await decrypt(key, row.key, row.encrypted),
    })));
}
export async function api(path, body, csrf) {
    const r = await fetch("/api/v1" + path, {
        method: body === undefined ? "GET" : "POST",
        headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "x-csrf-token": csrf } : {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok)
        throw Object.assign(new Error(data.error ?? "Request failed"), {
            status: r.status,
            data,
        });
    return data;
}
